import { FormatType } from '@revelio/replots';

import {
  DEFAULT_GRID_COLUMNS,
  MAX_REPORT_COMPETITORS,
} from '../../components/PageConfigProvider/constants';
import {
  PageType,
  type ReportPageConfig,
} from '../../components/PageConfigProvider/types';
import { FilterId } from '../../filters/types/filters';
import {
  buildFilterGroups,
  encodeFilterGroups,
} from '../../filters/utils/filterGroupsUtils';
import { buildReportPages } from './report-page-builder';

/** Preloads primary company + peer companies when opening the sample via ReportSampleLink (?filterGroups=...). */
const aiExposureAdoptionSampleFilterGroups = encodeFilterGroups(
  buildFilterGroups(
    [
      {
        groupKey: 'company-parameter',
        filterIds: [FilterId.COMPANY],
        defaultSelections: {
          [FilterId.COMPANY]: [
            { id: '8_256_1116644', name: 'Workday', levelId: 'company' },
          ],
        },
      },
      {
        groupKey: 'competitors-parameter',
        filterIds: [FilterId.COMPANY],
        defaultSelections: {
          [FilterId.COMPANY]: [
            { id: '8_257_919809', name: 'Snowflake', levelId: 'company' },
            { id: '8_257_1005273', name: 'MongoDB', levelId: 'company' },
            { id: '8_248_1010813', name: 'Palantir', levelId: 'company' },
            {
              id: '8_256_256277',
              name: 'Slack',
              levelId: 'company',
            },
          ],
        },
      },
    ],
    { primaryFilterId: FilterId.COMPANY }
  )
);

const executiveSummaryTextHtml = `## Executive Summary

### Overview

Revelio Labs' AI exposure and adoption report evaluates {{PARAM "Company"}}'s opportunities and risks related to AI technologies. It provides insights into workforce productivity, automation risks, and potential efficiency improvements by analyzing AI exposure and adoption across competitors, roles, and geographies.

### Key Findings

- {{#if (HAS_DATA "ai_exposure" entity=(PARAM "Company"))}}{{PARAM "Company"}}'s AI exposure is {{FORMAT (VALUE "ai_exposure" entity=(PARAM "Company") timeAggregation="latest") format=".1f"}}%, {{#if (gte (VALUE "ai_exposure" entity=(PARAM "Company") timeAggregation="latest") (AVG "ai_exposure" timeAggregation="latest" excludeEntity=(PARAM "Company")))}}higher{{else}}lower{{/if}} than the competitor peer group average.{{else}}AI exposure versus peers is not yet available for {{PARAM "Company"}} in this view.{{/if}}

- {{#if (HAS_DATA "ai_adoption" entity=(PARAM "Company"))}}{{PARAM "Company"}}'s AI adoption is {{FORMAT (VALUE "ai_adoption" entity=(PARAM "Company") timeAggregation="latest") format=".1f"}}%, {{#if (gte (VALUE "ai_adoption" entity=(PARAM "Company") timeAggregation="latest") (AVG "ai_adoption" timeAggregation="latest" excludeEntity=(PARAM "Company")))}}higher{{else}}lower{{/if}} than the competitor peer group average.{{else}}AI adoption versus peers is not yet available for {{PARAM "Company"}} in this view.{{/if}}

- {{#if (HAS_DATA "ai_exposure" segment="role" entity=(PARAM "Company"))}}Roles like '{{ARG_MAX "ai_exposure" segment="role" entity=(PARAM "Company") reduce="max"}}' have the highest AI exposure, while '{{ARG_MIN "ai_exposure" segment="role" entity=(PARAM "Company") reduce="min"}}' has the lowest.{{else}}Role-level AI exposure is not yet available in this view.{{/if}}

- {{#if (HAS_DATA "ai_adoption" segment="role" entity=(PARAM "Company"))}}'{{ARG_MAX "ai_adoption" segment="role" entity=(PARAM "Company") reduce="max"}}' and '{{ARG_MIN "ai_adoption" segment="role" entity=(PARAM "Company") reduce="min"}}' roles show notably high and low AI adoption rates respectively.{{else}}Role-level AI adoption highlights are not yet available in this view.{{/if}}

- {{#if (HAS_DATA "ai_adoption" segment="geography" entity=(PARAM "Company"))}}{{ARG_MAX "ai_adoption" segment="geography" entity=(PARAM "Company") reduce="max"}} shows the highest AI adoption rate among {{PARAM "Company"}}'s global operations.{{else}}Geography-level AI adoption is not yet available in this view.{{/if}}

### Highlighted Recommendations

- {{#if (HAS_DATA "ai_exposure" segment="role" entity=(PARAM "Company"))}}Increase AI adoption in roles with high exposure but low adoption, such as high-exposure roles where adoption lags.{{else}}When data is available, focus on increasing AI adoption in roles with high exposure but low adoption.{{/if}}

- {{#if (HAS_DATA "ai_adoption" segment="geography" entity=(PARAM "Company"))}}Focus on expanding AI integration in countries with lower adoption rates to boost productivity.{{else}}When geography data is available, expand AI integration in regions with lower adoption to boost productivity.{{/if}}

- {{#if (HAS_DATA "ai_adoption" segment="role" entity=(PARAM "Company"))}}Leverage roles with high AI adoption, like '{{ARG_MAX "ai_adoption" segment="role" entity=(PARAM "Company") reduce="max"}}', to drive AI initiatives.{{else}}Leverage high-adoption roles to drive AI initiatives once role-level data is available.{{/if}}

### Conclusion

{{#if (HAS_DATA "ai_exposure" entity=(PARAM "Company"))}}{{#if (HAS_DATA "ai_adoption" entity=(PARAM "Company"))}}{{PARAM "Company"}} demonstrates {{#if (gte (VALUE "ai_adoption" entity=(PARAM "Company") timeAggregation="latest") (AVG "ai_adoption" timeAggregation="latest" excludeEntity=(PARAM "Company")))}}a competitive edge in AI adoption compared to peers{{else}}room to grow in AI adoption compared to peers{{/if}}, but there is {{#if (gte (VALUE "ai_exposure" entity=(PARAM "Company") timeAggregation="latest") (AVG "ai_exposure" timeAggregation="latest" excludeEntity=(PARAM "Company")))}}also notable AI exposure that can be leveraged{{else}}room for growth in AI exposure{{/if}}. Strategic focus on roles and geographies with high potential for AI integration can enhance productivity and innovation.{{else}}{{PARAM "Company"}} can benefit from strategic focus on roles and geographies with high potential for AI integration to enhance productivity and innovation once adoption data are available.{{/if}}{{else}}{{PARAM "Company"}} can benefit from strategic focus on roles and geographies with high potential for AI integration to enhance productivity and innovation once exposure and adoption data are available.{{/if}}`;

const companyParam = { parameterId: 'company-parameter', grouped: false };
const competitorsParam = {
  parameterId: 'competitors-parameter',
  grouped: false,
};

const peerComparisonSnowflakeFields = [
  {
    name: 'rcid',
    label: 'Company',
    value: '',
    fieldType: 'number' as const,
    required: true,
    parameterId: 'company-parameter',
  },
  {
    name: 'competitor_rcids',
    label: 'Competitors',
    value: '',
    fieldType: 'number[]' as const,
    required: false,
    parameterId: 'competitors-parameter',
  },
];

const roleSnowflakeFields = [
  {
    name: 'rcid',
    label: 'Company',
    value: '',
    fieldType: 'number' as const,
    required: true,
    parameterId: 'company-parameter',
  },
];

export const reportAiExposureAdoption: ReportPageConfig = (() => {
  const { items, grid } = buildReportPages('ai-exposure-adoption', [
    [
      {
        title: '',
        position: { height: 32 },
        plot: {
          chartType: 'text',
          textHtml: executiveSummaryTextHtml,
          plotParameters: [companyParam],
        },
      },
    ],
    [
      {
        title: '',
        position: { height: 12 },
        plot: {
          chartType: 'text',
          textHtml: `## Industry overview

Comparing a company's AI exposure and adoption to its peers requires careful consideration of comparable organizations. We select peer companies using our competitors model, which accounts for factors such as industry, workforce composition, and company size.

Here, we show an overview of the peer group of {{PARAM "Company"}}. We select the 50 closest companies to {{PARAM "Company"}} using our competitors model and weight them relative to their current employee headcounts.`,
          plotParameters: [companyParam],
        },
      },
      {
        title: 'Composition of {{PARAM "Company"}}\'s peer group',
        position: { y: 12, height: 16 },
        plot: {
          chartType: 'text',
          textHtml: `*Placeholder: Peer group composition (weighted headcount by competitor) will appear here once treemap visualization and competitor headcount data are available.*`,
          plotParameters: [companyParam],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        title: '',
        position: { height: 14 },
        plot: {
          chartType: 'text',
          textHtml: `## AI Exposure compared to closest competitors

Comparing a company's AI exposure to that of its competitors and the industry average can give important information about both opportunities and risks stemming from AI.

Revelio Labs' activity taxonomy allows us to classify all activities and tasks extracted from position descriptions in online professional profiles into a standardized set of activities. We develop an AI exposure score for every job in our rich HR dataset, defined as the share of all work activities within a job that could potentially be performed by AI technology. The metric is expressed as a share varying between 0 and 100, and is measured for every person at a company with a job description. We aggregate these position-level AI exposure scores to the company level. Our company-level AI exposure metric thus measures the extent to which activities performed at the company could be done by AI technology instead of a human. Differences in AI exposure commonly stem from different role distributions across companies, but can also be driven by specific work tasks listed within an organization. Knowing where the company falls is important for both technology and innovation strategies as well as talent strategies.`,
          plotParameters: [companyParam],
        },
      },
      {
        title: 'AI Exposure of {{PARAM "Company"}} vs. closest competitors',
        info: {
          text: 'Share of tasks at company that could be done or helped by AI.',
        },
        position: { y: 12, height: 14 },
        plot: {
          chartType: 'bar',
          sort: 'DESC',
          format: FormatType.PERCENTAGE,
          showDataLabels: true,
          dataSource: 'snowflake',
          plotParameters: [companyParam, competitorsParam],
          snowflakeConfig: {
            savedQueryId: 'ai-exposure-peer-comparison',
            fields: peerComparisonSnowflakeFields,
          },
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        title: '',
        position: { height: 14 },
        plot: {
          chartType: 'text',
          textHtml: `## AI Adoption compared to closest competitors

In contrast to AI exposure, our AI adoption metric measures the extent to which employees utilize innovative AI technology in their workflows. We define AI adoption as the share of positions at a company whose descriptions in online professional profiles explicitly mention a list of AI-related keywords and technologies. Comparing a company's AI adoption to that of its competitors gives important information about the degree of innovation at an organization, and rate of adoption of AI tools. Not only does analyzing AI adoption allow for targeted procurement of the right tools, but it also can highlight the need for R&D or hiring to fill AI utilization gaps.`,
          plotParameters: [companyParam],
        },
      },
      {
        title: 'AI Adoption of {{PARAM "Company"}} vs. closest competitors',
        info: { text: 'Share of workers using AI tools.' },
        position: { y: 12, height: 14 },
        plot: {
          chartType: 'bar',
          sort: 'DESC',
          format: FormatType.PERCENTAGE,
          showDataLabels: true,
          dataSource: 'snowflake',
          plotParameters: [companyParam, competitorsParam],
          snowflakeConfig: {
            savedQueryId: 'ai-adoption-peer-comparison',
            fields: peerComparisonSnowflakeFields,
          },
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        title: '',
        position: { height: 12 },
        plot: {
          chartType: 'text',
          textHtml: `## AI Exposure by Role

Below we show the most and least AI exposed roles among the largest roles at {{PARAM "Company"}} by headcount. The metric shows average AI exposure by role, or equivalently, the average share of tasks within a role that could be performed by AI. Roles with high AI exposure may experience significant productivity benefits upon AI adoption, highlighting potential areas for AI integration efforts.`,
          plotParameters: [companyParam],
        },
      },
      {
        title: 'AI Exposure at {{PARAM "Company"}}: least exposed roles',
        info: {
          text: 'Average share of tasks within a role that could be performed by AI.',
        },
        position: { x: 0, y: 12, width: DEFAULT_GRID_COLUMNS / 2, height: 17 },
        plot: {
          chartType: 'bar',
          sort: 'ASC',
          format: FormatType.PERCENTAGE,
          color: 'var(--ui-red-400)',
          showDataLabels: true,
          dataSource: 'snowflake',
          plotParameters: [companyParam],
          snowflakeConfig: {
            savedQueryId: 'ai-exposure-by-role-least-exposed',
            fields: roleSnowflakeFields,
          },
        },
        isTitleManuallySet: true,
      },
      {
        title: 'AI Exposure at {{PARAM "Company"}}: most exposed roles',
        info: {
          text: 'Average share of tasks within a role that could be performed by AI.',
        },
        position: {
          x: DEFAULT_GRID_COLUMNS / 2,
          y: 12,
          width: DEFAULT_GRID_COLUMNS / 2,
          height: 17,
        },
        plot: {
          chartType: 'bar',
          sort: 'DESC',
          format: FormatType.PERCENTAGE,
          color: 'var(--ui-green-500)',
          showDataLabels: true,
          dataSource: 'snowflake',
          plotParameters: [companyParam],
          snowflakeConfig: {
            savedQueryId: 'ai-exposure-by-role-most-exposed',
            fields: roleSnowflakeFields,
          },
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        title: '',
        position: { height: 12 },
        plot: {
          chartType: 'text',
          textHtml: `## AI Adoption by Role

Below we show the roles with the highest and lowest AI adoption among the largest roles at {{PARAM "Company"}} by headcount. The metric shows average AI adoption by role, as measured by the average share of employees in a role that are adopting AI tools in their workflows.`,
          plotParameters: [companyParam],
        },
      },
      {
        title:
          'AI Adoption at {{PARAM "Company"}}: top and bottom roles adopting AI tools',
        position: { y: 12, height: 14 },
        plot: {
          chartType: 'text',
          textHtml: `*Placeholder: This chart will show top 10 and bottom 10 roles by AI adoption at {{PARAM "Company"}} once role-level \`ai_adoption\` and role taxonomy data are available.*`,
          plotParameters: [companyParam],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        title: '',
        position: { height: 14 },
        plot: {
          chartType: 'text',
          textHtml: `## AI possibility matrix by role

To explore all types of AI usage within an organization, we construct a possibility matrix that measures both AI adoption and exposure concurrently. The plot below shows the AI possibility matrix for the largest roles at {{PARAM "Company"}}. The x-axis shows AI exposure by role, the y-axis AI adoption by role. The four quadrants split the data among the average values of AI exposure and adoption.

Roles in the blue quadrant have above average AI adoption and exposure. These are roles that have a high potential for using AI for their activities, and workers in these roles are already using AI to make themselves more productive. Roles in the green quadrant have below average exposure, but high adoption. Roles in the yellow quadrant have low exposure and below average adoption. These roles should not concern themselves much with AI. Roles in the red quadrant have high exposure, but below average adoption. These roles are not actively adopting AI tools, but could see large efficiency benefits if they did, and may be at higher risk of automation from external forces.`,
          plotParameters: [companyParam],
        },
      },
      {
        title: 'AI Adoption and Exposure at {{PARAM "Company"}}',
        position: { y: 12, height: 18 },
        plot: {
          chartType: 'text',
          textHtml: `*Placeholder: Scatter/bubble chart (AI exposure vs AI adoption by role, bubble size = occupation count) with quadrant overlays will appear here once role-level \`ai_exposure\` and \`ai_adoption\` and role taxonomy are available.*`,
          plotParameters: [companyParam],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        title: '',
        position: { height: 12 },
        plot: {
          chartType: 'text',
          textHtml: `## AI Exposure by geography

Understanding how AI exposure varies across geographies can provide crucial information for a company's innovation and talent strategies. Varying geographical role distributions and specializations contribute to different AI exposures. Here, we show the average AI exposure of {{PARAM "Company"}} employees by country.`,
          plotParameters: [companyParam],
        },
      },
      {
        title:
          'Average AI exposure of {{PARAM "Company"}} employees by country',
        position: { y: 12, height: 14 },
        plot: {
          chartType: 'text',
          textHtml: `*Placeholder: Map or bar chart of average AI exposure by country will appear here once \`ai_exposure\` by geography (e.g. country) is available.*`,
          plotParameters: [companyParam],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        title: '',
        position: { height: 12 },
        plot: {
          chartType: 'text',
          textHtml: `## AI Adoption by geography

Understanding how AI adoption varies across geographies is another piece of important information, in particular when it comes to recognizing potential for productivity gains or lack thereof. Here, we show the average AI adoption of {{PARAM "Company"}} employees by country.`,
          plotParameters: [companyParam],
        },
      },
      {
        title:
          'Average AI adoption of {{PARAM "Company"}} employees by country',
        position: { y: 12, height: 14 },
        plot: {
          chartType: 'text',
          textHtml: `*Placeholder: Map or bar chart of average AI adoption by country will appear here once \`ai_adoption\` by geography (e.g. country) is available.*`,
          plotParameters: [companyParam],
        },
        isTitleManuallySet: true,
      },
    ],
  ]);

  return {
    type: PageType.REPORT,
    id: 'report-ai-exposure-adoption',
    name: 'AI Exposure and Adoption',
    owner: { id: 'revelio-labs', username: 'revelio-labs' },
    sampleFilterGroups: aiExposureAdoptionSampleFilterGroups,
    tabs: [
      {
        id: 'default',
        label: 'Default',
        tabContent: {
          id: 'ai-exposure-adoption-content',
          items,
          primaryFilter: {
            id: FilterId.COMPANY,
          },
          reportParameters: [
            {
              parameterId: 'company-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: true,
              name: 'Company',
            },
            {
              parameterId: 'competitors-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: false,
              name: 'Competitors',
              selectionLimit: MAX_REPORT_COMPETITORS,
            },
          ],
          grid,
          title: 'AI Exposure and Adoption',
          description:
            'AI is set to have an outsized effect on the workforce. How is your company impacted? The report tracks exposure to AI technology, highlighting both opportunities for greater effectiveness as well as vulnerabilities to automation.',
          secondaryFilters: [],
        },
        isActive: true,
      },
    ],
  };
})();
